import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { HAND_NOTICE_MS, handNoticeKey, openHandNotices, type RaisedHand } from "./raisedHand";

type RaisedHandNoticesProps = {
  hands: readonly RaisedHand[];
};

const RaisedHandNotices: React.FC<RaisedHandNoticesProps> = ({ hands }) => {
  const [dismissedKeys, setDismissedKeys] = useState<string[]>([]);
  const dismiss = useCallback((key: string) => {
    setDismissedKeys((current) => (current.includes(key) ? current : [...current, key]));
  }, []);

  const open = openHandNotices(hands, new Set(dismissedKeys));
  if (open.length === 0) return null;

  return (
    <div className="pointer-events-none fixed left-1/2 top-[calc(4.2rem+env(safe-area-inset-top,0px))] z-[80] flex w-[min(24rem,calc(100%-1.5rem))] -translate-x-1/2 flex-col gap-2 lg:top-[max(0.75rem,env(safe-area-inset-top,0px))]">
      {open.map((hand) => (
        <HandNoticeCard key={handNoticeKey(hand)} hand={hand} onDone={dismiss} />
      ))}
    </div>
  );
};

function HandNoticeCard({ hand, onDone }: { hand: RaisedHand; onDone: (key: string) => void }) {
  const key = handNoticeKey(hand);

  useEffect(() => {
    const timer = window.setTimeout(() => onDone(key), HAND_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [key, onDone]);

  return (
    <div
      role="status"
      className="pointer-events-auto relative overflow-hidden rounded-xl border border-amber-300/80 bg-amber-50 text-sm text-amber-950 shadow-lg"
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <span aria-hidden="true" className="text-base leading-none">✋</span>
        <p className="min-w-0 flex-1 font-medium">{hand.name} pediu para falar</p>
        <button
          type="button"
          className="rounded-full p-1 text-amber-900 hover:bg-amber-200"
          aria-label={`Fechar aviso de ${hand.name}`}
          onClick={() => onDone(key)}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="h-1 bg-amber-200/80" aria-hidden="true">
        <div
          className="h-full origin-left bg-amber-600"
          style={{ animation: `constela-hand-notice ${HAND_NOTICE_MS}ms linear forwards` }}
        />
      </div>
      <style>{`@keyframes constela-hand-notice { from { transform: scaleX(1); } to { transform: scaleX(0); } }`}</style>
    </div>
  );
}

export default RaisedHandNotices;
